import { faEye, faEyeSlash, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useComposedRefs } from '@reach/utils';
import type { HTMLMotionProps } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';

import styles from './Input.module.scss';

interface Validator {
  message: string;
  priority: number;
  validate: (value: string) => boolean;
}

interface InputProps extends Omit<HTMLMotionProps<'input' | 'textarea'>, 'onChange'> {
  label?: string;
  alwaysMask?: boolean;
  placeholder?: string;
  type?: 'email' | 'number' | 'password' | 'tel' | 'text' | 'url' | 'date';
  value: string;
  id?: string;
  validators?: Validator[];
  errorMessage?: string;
  showError?: boolean;
  showErrorAsWarning?: boolean;
  contained?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  theme?: 'light' | 'dark';
  multiLine?: boolean;
  enableClearButton?: boolean;
  hideAllErrorMessages?: boolean;
  onChange: (newValue: string) => void;
}

const Input = ({
  ref: forwardedRef,
  label,
  alwaysMask = false,
  placeholder,
  type = 'text',
  value = '',
  id,
  errorMessage,
  showError = false,
  showErrorAsWarning = false,
  contained = false,
  onChange,
  disabled = false,
  multiLine = false,
  size = 'medium',
  theme = 'light',
  enableClearButton = false,
  hideAllErrorMessages = false,
  ...rest
}: InputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const [canSeePassword, setCanSeePassword] = useState(false);
  const inputRef = useRef(null);
  const ref = useComposedRefs(inputRef, forwardedRef);

  const inputType = type !== 'password' ? type : canSeePassword ? 'text' : 'password';
  const ContainerComponent = label ? 'label' : 'div';

  function renderInput() {
    if (multiLine) {
      return (
        <motion.textarea
          value={value}
          data-dd-privacy={alwaysMask ? 'mask' : undefined}
          className={styles['input-el']}
          onChange={(event) => onChange(event.target.value)}
          data-has-value={value.length > 0}
          ref={ref}
          id={id}
          disabled={disabled}
          placeholder={placeholder}
          {...(rest as HTMLMotionProps<'textarea'>)}
        />
      );
    } else {
      return (
        <motion.input
          type={inputType}
          data-dd-privacy={alwaysMask ? 'mask' : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          data-has-value={value.length > 0}
          className={styles['input-el']}
          ref={ref}
          id={id}
          disabled={disabled}
          placeholder={placeholder}
          {...(rest as HTMLMotionProps<'input'>)}
        />
      );
    }
  }

  return (
    <div
      className={styles.input}
      data-contained={contained || multiLine}
      data-theme={theme}
      data-size={size}
      data-error={showError}
    >
      <ContainerComponent
        className={styles['input-container']}
        data-has-floating-label={!!label}
        data-hide-all-errors={hideAllErrorMessages}
      >
        {renderInput()}
        {label && <span className={styles['label-text']}>{label}</span>}
        {type === 'password' && (
          <button
            className={styles['reveal-password-toggle-container']}
            onClick={() => setCanSeePassword(!canSeePassword)}
            type="button"
          >
            {!canSeePassword && (
              <span>
                <FontAwesomeIcon icon={faEye} />
              </span>
            )}
            {canSeePassword && (
              <span>
                <FontAwesomeIcon icon={faEyeSlash} />
              </span>
            )}
          </button>
        )}
        {showError && (
          <span className={styles['error-text']} data-is-warning={showErrorAsWarning}>
            {errorMessage}
          </span>
        )}
        <AnimatePresence>
          {enableClearButton && !!value && (
            <motion.button
              className={styles['clear-btn']}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              role="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange('');
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </motion.button>
          )}
        </AnimatePresence>
      </ContainerComponent>
    </div>
  );
};

Input.displayName = 'Input';

export default Input;
